import { useQuery } from "react-query";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const useGetList = (
  schoolId,
  trainingId,
  accessToken,
  page = 1,
  pageSize = 30,
  filter
) => {
  return useQuery(
    ["contracts", schoolId, trainingId, accessToken, page, pageSize, filter],
    async () => {
      let parentsIds = filter?.parents_ids;
      if (parentsIds === undefined) {
        parentsIds = "";
      }
      let name = filter?.name;
      if (name === undefined) {
        name = "";
      }
      let notFullyPaid = filter?.not_fully_paid;
      if (notFullyPaid === undefined || notFullyPaid === "") {
        notFullyPaid = "";
      } else if (notFullyPaid === "true") {
        notFullyPaid = "true";
      } else if (notFullyPaid === "false") {
        notFullyPaid = "false";
      } else if (notFullyPaid === "fully_paid") {
        notFullyPaid = "fully_paid";
      } else {
        notFullyPaid = "";
      }
      try {
        const response = await fetch(
          BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/contracts?page=${page}&pageSize=${pageSize}&parents_ids=${parentsIds}&name=${name}&not_fully_paid=${notFullyPaid}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Failed to fetch contracts!");
        }
        if (response.status >= 299) {
          throw new Error("Erreur de récupération de la liste des contracts!");
        }
        const result = await response.json();
        return result;
      } catch (error) {
        // Handle the error
        console.error("Error fetching contracts:", error);
        throw error;
      }
    }
  );
};

const GetOne = (schoolId, trainingId, accessToken, courseId) => {
  return useQuery(
    ["contract", schoolId, trainingId, accessToken, courseId],
    async () => {
      try {
        const response = await fetch(
          BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/contracts/${courseId}`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Erreur de récupération du contract!");
        }
        const result = await response.json();
        return result;
      } catch (error) {
        throw error;
      }
    }
  );
};

const GetActivitiesHistory = (schoolId, trainingId, accessToken, contractId) => {
  return useQuery(
    ["contract-activities-history", schoolId, trainingId, accessToken, contractId],
    async () => {
      try {
        const response = await fetch(
          BACKEND_URL +
          "/api/v1/schools/" +
          schoolId +
          "/trainings/" +
          trainingId +
          "/contracts/" +
          contractId +
          "/activity-logs",
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Erreur de récupération de l'historique des activités du contrat!`);
        }
        let actualData = await response.json();
        return actualData;
      } catch (err) {
        throw err;
      }
    }
  );
};

const CreateOne = async (schoolId, trainingId, accessToken, data) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/contracts",
      {
        method: "POST",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de création du contract!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateOne = async (schoolId, trainingId, accessToken, coursId, data) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/contracts/" +
      coursId,
      {
        method: "PATCH",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification du contract!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const UpdateTotalAmount = async (schoolId, trainingId, accessToken, contractId, totalAmount) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/contracts/" +
      contractId +
      "/update_total_amount",
      {
        method: "PATCH",
        body: JSON.stringify({
          total_amount: totalAmount
        }),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification du montant total du contract!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
}

/**
 * Updates the students associated with a contract
 * @param {string} schoolId - The school ID
 * @param {string} trainingId - The training ID
 * @param {string} accessToken - The access token
 * @param {string} contractId - The contract ID
 * @param {Object} data - The data to send
 * @param {string} data.action - The action to perform: "ADD" or "REMOVE"
 * @param {Array<string>} data.students_ids - Array of student IDs to add or remove
 * @returns {Promise<Object>} The response data
 */
const UpdateContractStudents = async (schoolId, trainingId, accessToken, contractId, data) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/contracts/" +
      contractId +
      "/students",
      {
        method: "POST",
        body: JSON.stringify(data),
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de modification de la liste des élèves du contract!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
}

export const useListNonAddedStudents = (
  schoolId,
  trainingId,
  accessToken,
  contractId
) => {
  return useQuery(
    ["contracts", "non-added-students", schoolId, trainingId, accessToken, contractId],
    async () => {
      try {
        const response = await fetch(
          BACKEND_URL +
          "/api/v1/schools/" +
          schoolId +
          "/trainings/" +
          trainingId +
          "/contracts/" +
          contractId +
          "/non-added-students",
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );
        if (!response.ok) {
          throw new Error(`Erreur de récupération de la liste des élèves non ajoutés au contract!`);
        }
        let actualData = await response.json();
        return actualData;
      } catch (err) {
        console.error("Error fetching non-added students:", err);
        throw err;
      }
    },
    {
      // Keep previous data while loading new data to prevent flickering
      keepPreviousData: false,
      // Refetch on window focus only if data is stale
      refetchOnWindowFocus: true,
    }
  );
};

const DeleteOne = async (schoolId, trainingId, accessToken, courseId) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      "/api/v1/schools/" +
      schoolId +
      "/trainings/" +
      trainingId +
      "/contracts/" +
      courseId,
      {
        method: "DELETE",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );
    if (!response.ok) {
      throw new Error(`Erreur de suppression du contract!`);
    }
    let actualData = await response.json();
    return actualData;
  } catch (err) {
    throw err;
  }
};

const GetPaymentSituationEmailPreview = (schoolId, trainingId, accessToken, contractId, enabled = false) => {
  return useQuery(
    ["contract-payment-situation-preview", schoolId, trainingId, accessToken, contractId],
    async () => {
      try {
        const response = await fetch(
          BACKEND_URL +
          `/api/v1/schools/${schoolId}/trainings/${trainingId}/contracts/${contractId}/payment-situation-email-preview`,
          {
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
              Authorization: "Bearer " + accessToken,
            },
          }
        );

        if (!response.ok) {
          throw new Error("Erreur de chargement de l'aperçu du mail!");
        }
        const result = await response.json();
        return result;
      } catch (error) {
        throw error;
      }
    },
    {
      enabled: enabled && !!schoolId && !!trainingId && !!contractId,
    }
  );
};

const SendPaymentSituationEmail = async (schoolId, trainingId, accessToken, contractId) => {
  try {
    const response = await fetch(
      BACKEND_URL +
      `/api/v1/schools/${schoolId}/trainings/${trainingId}/contracts/${contractId}/send-payment-situation-email`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + accessToken,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData?.error || "Erreur de l'envoi de l'e-mail!");
    }
    const result = await response.json();
    return result;
  } catch (error) {
    throw error;
  }
};

const contractsAPI = {
  useGetList,
  GetOne,
  GetActivitiesHistory,
  CreateOne,
  UpdateOne,
  UpdateTotalAmount,
  UpdateContractStudents,
  useListNonAddedStudents,
  DeleteOne,
  GetPaymentSituationEmailPreview,
  SendPaymentSituationEmail,
};

export default contractsAPI;
